import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader } from "@/components/PageUI";
import { toast } from "sonner";
import { LogOut, CalendarCheck, Star, DollarSign, Megaphone, Swords, CreditCard, Send, MessageSquare, FileText, Upload, ShieldCheck, RefreshCw, CheckCheck, FolderOpen, Download, Wallet } from "lucide-react";
import { TeamPhotos } from "@/components/TeamPhotos";
import { PayOptions } from "@/components/PayOptions";

export default function ParentPortal({ mode = "parent" }) {
  const { user, logout } = useAuth();
  const isPlayer = mode === "player";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [payHistory, setPayHistory] = useState(null);
  const [paying, setPaying] = useState("");
  const [pmsgs, setPmsgs] = useState([]);
  const [pText, setPText] = useState("");
  const [autopay, setAutopay] = useState({ enabled: false, last4: "" });
  const [docReqs, setDocReqs] = useState([]);
  const [uploadingReq, setUploadingReq] = useState("");
  const [tourneys, setTourneys] = useState({ tournaments: [], children: [] });
  const [regBusy, setRegBusy] = useState("");
  const [store, setStore] = useState({ products: [] });
  const [buyBusy, setBuyBusy] = useState("");
  const [sizeSel, setSizeSel] = useState({});
  const [polls, setPolls] = useState([]);
  const [library, setLibrary] = useState([]);
  const [support, setSupport] = useState({ email: "" });
  const [fb, setFb] = useState({ type: "general", subject: "", message: "" });
  const [wallet, setWallet] = useState({ balance: 0, currency: "USD", enabled: false, transactions: [] });
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmt, setTopupAmt] = useState(50);
  const [topupBusy, setTopupBusy] = useState("");

  const load = () => api.get("/parent/overview").then((r) => setData(r.data)).catch(() => setData({ children: [], fees_due: [], announcements: [] }));
  const loadMsgs = () => api.get("/parent/messages").then((r) => setPmsgs(r.data)).catch(() => {});
  const loadAutopay = () => api.get("/parent/autopay/status").then((r) => setAutopay(r.data)).catch(() => {});
  const loadReqs = () => api.get("/parent/document-requests").then((r) => setDocReqs(r.data)).catch(() => {});
  const loadTourneys = () => api.get("/parent/tournaments").then((r) => setTourneys(r.data)).catch(() => {});
  const loadStore = () => api.get("/parent/store").then((r) => setStore(r.data)).catch(() => {});
  const loadPolls = () => api.get("/polls").then((r) => setPolls(r.data)).catch(() => {});
  const loadLibrary = () => api.get("/library").then((r) => setLibrary(r.data)).catch(() => {});
  const votePoll = async (pollId, optionId) => {
    try { await api.post(`/polls/${pollId}/vote`, { option_id: optionId }); loadPolls(); toast.success("Vote recorded!"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const submitFeedback = async () => {
    if (!fb.subject.trim() || !fb.message.trim()) return toast.error("Subject and message are required");
    try { await api.post("/feedback", fb); setFb({ type: "general", subject: "", message: "" }); toast.success("Thanks! Your message was sent."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => {
    load(); loadMsgs(); loadAutopay(); loadReqs(); loadTourneys(); loadStore(); loadPolls(); loadLibrary();
    api.get("/parent/payments").then((r) => setPayHistory(r.data)).catch(() => {});
    api.get("/support/info").then((r) => setSupport({ email: r.data.support_email })).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.get("autopay") === "1" && params.get("session_id")) {
      api.post("/parent/autopay/confirm", { session_id: params.get("session_id") })
        .then(() => { toast.success("Autopay enabled!"); loadAutopay(); })
        .catch(() => toast.error("Could not enable autopay"))
        .finally(() => window.history.replaceState(null, "", isPlayer ? "/portal" : "/parent"));
    }
  }, []);

  useEffect(() => { if (data?.club?.id) loadWallet(data.club.id); }, [data?.club?.id]);

  const setupAutopay = async () => {
    try {
      const { data: res } = await api.post("/parent/autopay/setup", { origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d && !String(d).includes("Cloudflare") ? d : "Card setup is temporarily unavailable. Please try again shortly, or contact your club.");
    }
  };
  const disableAutopay = async () => { await api.delete("/parent/autopay"); toast.success("Autopay turned off"); loadAutopay(); };

  const loadWallet = (clubId) => api.get(`/payments/wallet?club_id=${clubId}`).then((r) => setWallet(r.data)).catch(() => {});

  const registerTournament = async (t, playerId, method = "card") => {
    setRegBusy(`${t.id}_${playerId}:${method}`);
    try {
      if (method === "wallet") {
        await api.post("/payments/wallet/pay", { kind: "tournament", club_id: club.id, tournament_id: t.id, player_id: playerId });
        toast.success("Registered — paid from your balance"); loadTourneys(); loadWallet(club.id); setRegBusy("");
        return;
      }
      const { data } = await api.post("/payments/tournament-checkout", { tournament_id: t.id, player_id: playerId, origin_url: window.location.origin, payment_method: method });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setRegBusy(""); }
  };

  const buyProduct = async (p, method = "card") => {
    const size = sizeSel[p.id] || (p.sizes?.length ? p.sizes[0] : "");
    setBuyBusy(`${p.id}:${method}`);
    try {
      if (method === "wallet") {
        await api.post("/payments/wallet/pay", { kind: "store", club_id: club.id, product_id: p.id, size });
        toast.success("Purchased — paid from your balance"); loadStore(); loadWallet(club.id); setBuyBusy("");
        return;
      }
      const { data } = await api.post("/payments/store-checkout", { product_id: p.id, size, buyer_name: user?.name, buyer_email: user?.email, origin_url: window.location.origin, payment_method: method });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBuyBusy(""); }
  };

  const uploadForRequest = (req, file) => {
    if (!file) return;
    if (file.size > 6_000_000) return toast.error("File too large (max 6MB)");
    setUploadingReq(req.id);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/players/${req.player_id}/documents`, { name: file.name, category: req.category, content_type: file.type, data: reader.result, request_id: req.id });
        toast.success("Document submitted"); loadReqs();
      } catch (e) { toast.error(apiErr(e)); } finally { setUploadingReq(""); }
    };
    reader.readAsDataURL(file);
  };

  const sendMsg = async () => {
    if (!pText.trim()) return;
    try { await api.post("/parent/messages", { text: pText }); setPText(""); loadMsgs(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const payFee = async (fee, method = "card") => {
    setPaying(`${fee.id}:${method}`);
    try {
      if (method === "wallet") {
        await api.post("/payments/wallet/pay", { kind: "fee", club_id: club.id, fee_id: fee.id, player_id: fee.player_id });
        toast.success("Paid from your balance"); load(); loadWallet(club.id); setPaying("");
        return;
      }
      const { data: res } = await api.post("/payments/fee-checkout", {
        club_id: fee.club_id, fee_id: fee.id, player_id: fee.player_id,
        fee_type: fee.fee_type, amount: fee.amount, description: fee.description || "",
        origin_url: window.location.origin, payment_method: method,
      });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setPaying(""); }
  };

  const topUp = async (method) => {
    if (!topupAmt || topupAmt < 1) return toast.error("Enter an amount to top up");
    setTopupBusy(method);
    try {
      const { data } = await api.post("/payments/wallet/topup", { club_id: club.id, amount: Number(topupAmt), origin_url: window.location.origin, payment_method: method });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setTopupBusy(""); }
  };

  if (!data) return <div className="min-h-screen bg-background"><Loader /></div>;
  const club = data.club;
  const cur = club?.currency || "USD";
  const wb = wallet.enabled ? wallet.balance : null;
  const lastSeenIdx = pmsgs.reduce((acc, m, i) => (m.from_parent && m.seen ? i : acc), -1);

  return (
    <div className="min-h-screen bg-background grain">
      <header className="h-16 border-b border-border glass sticky top-0 z-20 flex items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-2">
          {club?.logo ? <img src={club.logo} alt="" className="h-8 w-8 rounded-md object-cover border border-border" /> : <Logo size={30} showText={false} />}
          <div>
            <p className="font-heading font-bold leading-tight">{club?.name || (isPlayer ? "Players Portal" : "Parent Portal")}</p>
            <p className="text-[11px] text-muted-foreground">{isPlayer ? "Players Portal" : "Parent Portal"}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground hidden sm:inline">{user?.name}</span>
          <button onClick={doLogout} className="text-muted-foreground hover:text-destructive" data-testid="parent-logout-btn"><LogOut size={18} /></button>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-5xl mx-auto animate-fade-up">
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight mb-1">Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="text-muted-foreground mb-6">{isPlayer ? "Track your training, schedule and payments." : "Track your children, schedules and payments."}</p>

        {wallet.enabled && data.children.length > 0 && (
          <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-5 sm:p-6 mb-6" data-testid="wallet-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2"><Wallet size={18} className="text-emerald-500" /><h3 className="font-heading font-semibold text-lg">Club wallet</h3></div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Balance</p>
                <p className="font-heading font-extrabold text-2xl text-emerald-600" data-testid="wallet-balance">{cur} {wallet.balance.toFixed(2)}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Top up once, then pay fees, tournaments and store items straight from your balance with <b>zero extra processing fees</b> — ideal for small payments where card fees would otherwise take ~7%.</p>
            <Button size="sm" className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white border-0" onClick={() => setTopupOpen((v) => !v)} data-testid="wallet-topup-toggle"><Wallet size={14} className="mr-1.5" /> {topupOpen ? "Close" : "Top up balance"}</Button>
            {topupOpen && (
              <div className="mt-4 rounded-xl border border-border bg-card p-4 space-y-3" data-testid="wallet-topup-panel">
                <div className="flex flex-wrap gap-2">
                  {[20, 50, 100, 200].map((a) => (
                    <button key={a} onClick={() => setTopupAmt(a)} className={`h-9 px-3 rounded-lg border text-sm ${Number(topupAmt) === a ? "border-emerald-500 bg-emerald-500/10 text-emerald-600" : "border-border"}`} data-testid={`wallet-preset-${a}`}>{cur} {a}</button>
                  ))}
                  <Input type="number" min="1" value={topupAmt} onChange={(e) => setTopupAmt(e.target.value)} className="h-9 w-28" data-testid="wallet-amount-input" />
                </div>
                <PayOptions club={club} amount={Number(topupAmt) || 0} paying={topupBusy} onPay={topUp} testId="wallet-topup" />
              </div>
            )}
            {wallet.transactions?.length > 0 && (
              <div className="mt-4 space-y-1.5" data-testid="wallet-ledger">
                {wallet.transactions.slice(0, 5).map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{(tx.created_at || "").slice(0, 10)} · {tx.label}</span>
                    <span className={tx.amount >= 0 ? "text-emerald-600 font-medium" : "text-foreground"}>{tx.amount >= 0 ? "+" : ""}{cur} {Math.abs(tx.amount).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {data.children.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center" data-testid="parent-no-children">
            <p className="font-heading font-semibold text-lg">{isPlayer ? "Your profile isn't linked yet" : "No athletes linked yet"}</p>
            <p className="text-muted-foreground text-sm mt-1">{isPlayer ? "Your coach links your profile to your email address. Contact your club if this seems wrong." : "Your coach links athletes to your email address. Contact your club if this seems wrong."}</p>
          </div>
        )}

        {data.children.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div className="rounded-2xl border border-border bg-card p-5 sm:p-6" data-testid="autopay-card">
              <div className="flex items-center gap-2 mb-3"><RefreshCw size={18} className="text-secondary" /><h3 className="font-heading font-semibold text-lg">Autopay</h3></div>
              {autopay.enabled ? (
                <>
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5"><ShieldCheck size={15} className="text-secondary" /> Card ending •••• {autopay.last4 || "----"} on file</p>
                  <p className="text-xs text-muted-foreground mt-1">Recurring tuition is charged automatically.</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button size="sm" className="brand-gradient text-white border-0 hover:opacity-90" onClick={setupAutopay} data-testid="autopay-change-btn"><CreditCard size={14} className="mr-1.5" /> Change card</Button>
                    <Button size="sm" variant="outline" onClick={disableAutopay} data-testid="autopay-disable-btn">Turn off autopay</Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">Save a card so monthly tuition charges automatically — no more manual payments.</p>
                  <Button size="sm" className="mt-4 brand-gradient text-white border-0 hover:opacity-90" onClick={setupAutopay} data-testid="autopay-setup-btn"><CreditCard size={14} className="mr-1.5" /> Set up autopay</Button>
                </>
              )}
            </div>

            {docReqs.length > 0 && (
              <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-5 sm:p-6" data-testid="doc-requests-card">
                <div className="flex items-center gap-2 mb-3"><FileText size={18} className="text-amber-400" /><h3 className="font-heading font-semibold text-lg">Documents requested</h3></div>
                <div className="space-y-3">
                  {docReqs.map((r) => (
                    <div key={r.id} className="flex items-center justify-between gap-3" data-testid={`doc-req-${r.id}`}>
                      <div><p className="font-medium text-sm">{r.name}</p><p className="text-xs text-muted-foreground">for {r.player_name}</p></div>
                      <label className="shrink-0">
                        <input type="file" className="hidden" onChange={(e) => uploadForRequest(r, e.target.files?.[0])} data-testid={`doc-req-upload-${r.id}`} />
                        <span className="inline-flex items-center h-9 px-3 rounded-lg brand-gradient text-white text-sm font-medium cursor-pointer"><Upload size={14} className="mr-1.5" /> {uploadingReq === r.id ? "..." : "Upload"}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {data.fees_due?.length > 0 && (
          <div className="rounded-2xl border border-primary/40 bg-primary/5 p-5 sm:p-6 mb-6" data-testid="parent-fees">
            <div className="flex items-center gap-2 mb-4"><DollarSign size={18} className="text-primary" /><h3 className="font-heading font-semibold text-lg">Payments due</h3></div>
            <div className="space-y-3">
              {data.fees_due.map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-3 bg-card rounded-xl p-4 border border-border" data-testid={`parent-fee-${f.id}`}>
                  <div><p className="font-medium text-sm">{f.player_name} · {f.fee_type}</p><p className="text-xs text-muted-foreground">Due {f.due_date || "—"}</p></div>
                  <div className="flex items-center gap-3">
                    <span className="font-heading font-bold">{cur} {f.amount?.toFixed(2)}</span>
                    <PayOptions club={club} amount={f.amount} compact walletBalance={wb} paying={paying.startsWith(`${f.id}:`) ? paying.split(":")[1] : ""} onPay={(m) => payFee(f, m)} testId={`pay-fee-${f.id}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {payHistory?.history?.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 mb-6" data-testid="payment-history-card">
            <div className="flex items-center gap-2 mb-4"><DollarSign size={18} className="text-secondary" /><h3 className="font-heading font-semibold text-lg">Payment history</h3></div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-xl border border-border p-4"><p className="text-xs text-muted-foreground">Total paid</p><p className="font-heading font-extrabold text-2xl text-secondary" data-testid="total-paid">{cur} {payHistory.total_paid?.toFixed(2)}</p></div>
              <div className="rounded-xl border border-border p-4"><p className="text-xs text-muted-foreground">Outstanding</p><p className="font-heading font-extrabold text-2xl" data-testid="total-outstanding">{cur} {payHistory.total_outstanding?.toFixed(2)}</p></div>
            </div>
            <div className="divide-y divide-border">
              {payHistory.history.map((h) => (
                <div key={h.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`history-${h.id}`}>
                  <div>
                    <p className="text-sm font-medium">{h.fee_type}{h.player_name ? ` · ${h.player_name}` : ""}</p>
                    <p className="text-xs text-muted-foreground">{h.status === "paid" ? `Paid ${(h.paid_at || "").slice(0, 10)}` : `Due ${h.due_date || "—"}`}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-heading font-bold text-sm">{cur} {h.amount?.toFixed(2)}</span>
                    {h.status === "paid" && <span className="text-[10px] uppercase tracking-wide bg-secondary/15 text-secondary px-1.5 py-0.5 rounded">Paid</span>}
                    {h.status === "overdue" && <span className="text-[10px] uppercase tracking-wide bg-destructive/15 text-destructive px-1.5 py-0.5 rounded">Overdue</span>}
                    {h.status === "pending" && <span className="text-[10px] uppercase tracking-wide bg-amber-400/15 text-amber-500 px-1.5 py-0.5 rounded">Pending</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tourneys.tournaments?.length > 0 && (
          <div className="rounded-2xl border border-secondary/40 bg-secondary/5 p-5 sm:p-6 mb-6" data-testid="parent-tournaments">
            <div className="flex items-center gap-2 mb-4"><Swords size={18} className="text-secondary" /><h3 className="font-heading font-semibold text-lg">Tournaments & events</h3></div>
            <div className="space-y-4">
              {tourneys.tournaments.map((t) => (
                <div key={t.id} className="bg-card rounded-xl p-4 border border-border" data-testid={`parent-tournament-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-sm">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.date || "Date TBD"}{t.location ? ` · ${t.location}` : ""}</p>
                      {t.description && <p className="text-xs text-muted-foreground mt-1">{t.description}</p>}
                    </div>
                    <span className="font-heading font-bold shrink-0">{cur} {t.fee?.toFixed(2)}</span>
                  </div>
                  <div className="mt-3 space-y-2">
                    {t.registered?.map((r) => (
                      <div key={r.player_id} className="flex items-center justify-between gap-2">
                        <span className="text-sm">{r.player_name}</span>
                        {r.status === "paid" ? (
                          <span className="text-xs text-secondary flex items-center gap-1" data-testid={`trn-paid-${t.id}-${r.player_id}`}><ShieldCheck size={13} /> Registered</span>
                        ) : (
                          <PayOptions club={club} amount={t.fee || 0} compact walletBalance={wb} paying={regBusy.startsWith(`${t.id}_${r.player_id}:`) ? regBusy.split(":")[1] : ""} onPay={(m) => registerTournament(t, r.player_id, m)} testId={`trn-register-${t.id}-${r.player_id}`} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {store.products?.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 mb-6" data-testid="parent-store">
            <div className="flex items-center gap-2 mb-4"><DollarSign size={18} className="text-secondary" /><h3 className="font-heading font-semibold text-lg">Club store</h3></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {store.products.map((p) => (
                <div key={p.id} className="rounded-xl border border-border overflow-hidden" data-testid={`store-product-${p.id}`}>
                  {p.image ? <img src={p.image} alt="" className="h-36 w-full object-cover" /> : <div className="h-36 w-full bg-accent flex items-center justify-center text-muted-foreground text-sm">No image</div>}
                  <div className="p-4 space-y-2">
                    <div><p className="font-medium text-sm">{p.name}</p>{p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}</div>
                    <p className="font-heading font-bold">{cur} {p.price?.toFixed(2)}</p>
                    {p.sizes?.length > 0 && (
                      <select value={sizeSel[p.id] || p.sizes[0]} onChange={(e) => setSizeSel((s) => ({ ...s, [p.id]: e.target.value }))} className="w-full h-9 rounded-lg bg-background border border-border text-sm px-2" data-testid={`store-size-${p.id}`}>
                        {p.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )}
                    <PayOptions club={club} amount={p.price || 0} compact walletBalance={wb} paying={buyBusy.startsWith(`${p.id}:`) ? buyBusy.split(":")[1] : ""} onPay={(m) => buyProduct(p, m)} testId={`buy-product-${p.id}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {library.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 mb-6" data-testid="parent-library">
            <div className="flex items-center gap-2 mb-4"><FolderOpen size={18} className="text-secondary" /><h3 className="font-heading font-semibold text-lg">Club documents & media</h3></div>
            <div className="divide-y divide-border">
              {library.map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`parent-lib-${f.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{f.name}</p>
                    <p className="text-xs text-muted-foreground">{f.category}{f.size ? ` · ${f.size > 1048576 ? (f.size / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(f.size / 1024)) + " KB"}` : ""}</p>
                  </div>
                  <a href={`${API}/library/${f.id}/file?auth=${f.url_token}&download=1`} className="shrink-0 inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid={`parent-lib-download-${f.id}`}><Download size={14} /> Download</a>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          {data.children.map((c) => (
            <div key={c.player.id} className="rounded-2xl border border-border bg-card p-5 sm:p-6" data-testid={`parent-child-${c.player.id}`}>
              <div className="flex items-center gap-4 mb-5">
                {c.player.photo ? <img src={c.player.photo} alt="" className="h-14 w-14 rounded-full object-cover border border-border" /> : <div className="h-14 w-14 rounded-full brand-gradient flex items-center justify-center text-white text-xl font-bold">{c.player.name[0]}</div>}
                <div>
                  <p className="font-heading font-bold text-lg">{c.player.name}</p>
                  <p className="text-sm text-muted-foreground">{c.player.position || "Athlete"} {c.player.jersey_number && `· #${c.player.jersey_number}`}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-xl border border-border p-4 text-center"><CalendarCheck size={16} className="mx-auto text-secondary mb-1" /><p className="font-heading font-extrabold text-2xl">{c.attendance_pct}%</p><p className="text-xs text-muted-foreground">Attendance</p></div>
                <div className="rounded-xl border border-border p-4 text-center"><Star size={16} className="mx-auto text-primary mb-1" /><p className="font-heading font-extrabold text-2xl">{c.overall_rating || "—"}</p><p className="text-xs text-muted-foreground">Rating</p></div>
                <div className="rounded-xl border border-border p-4 text-center"><Swords size={16} className="mx-auto text-secondary mb-1" /><p className="font-heading font-extrabold text-2xl">{c.upcoming?.length || 0}</p><p className="text-xs text-muted-foreground">Upcoming</p></div>
                <div className="rounded-xl border border-border p-4 text-center"><Star size={16} className="mx-auto text-primary mb-1" /><p className="font-heading font-extrabold text-2xl">{Object.keys(c.stats || {}).length}</p><p className="text-xs text-muted-foreground">Stat types</p></div>
              </div>
              {c.upcoming?.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Upcoming schedule</p>
                  {c.upcoming.map((m) => (
                    <div key={m.id} className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0">
                      <span>vs {m.opponent} · {m.location || "TBD"}</span><span className="text-muted-foreground">{m.date || "—"}</span>
                    </div>
                  ))}
                </div>
              )}
              {c.player.team_id && (
                <div className="mt-5 pt-5 border-t border-border" data-testid={`member-photos-${c.player.id}`}>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">Team photos</p>
                  <TeamPhotos teamId={c.player.team_id} canUpload />
                </div>
              )}
            </div>
          ))}
        </div>

        {data.children.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 mt-6" data-testid="parent-chat-card">
            <div className="flex items-center gap-2 mb-4"><MessageSquare size={18} className="text-secondary" /><h3 className="font-heading font-semibold text-lg">Message your coach</h3></div>
            <div className="max-h-64 overflow-y-auto space-y-2 mb-3">
              {pmsgs.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Start a conversation with your coach.</p>}
              {pmsgs.map((m, i) => (
                <div key={m.id} className={`flex flex-col ${m.from_parent ? "items-end" : "items-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${m.from_parent ? "brand-gradient text-white" : "bg-accent"}`}>
                    {!m.from_parent && <p className="text-xs font-semibold text-secondary mb-0.5">{m.author}</p>}
                    <p className="text-sm">{m.text}</p>
                  </div>
                  {i === lastSeenIdx && (
                    <p className="text-[11px] text-muted-foreground mt-0.5 mr-1 flex items-center gap-1" data-testid="parent-msg-seen"><CheckCheck size={12} className="text-secondary" /> Seen</p>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={pText} onChange={(e) => setPText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder="Type a message to your coach..." data-testid="parent-msg-input" />
              <Button onClick={sendMsg} className="brand-gradient text-white border-0" data-testid="parent-msg-send"><Send size={16} /></Button>
            </div>
          </div>
        )}

        {polls.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3"><Star size={18} className="text-secondary" /><h3 className="font-heading font-semibold text-lg">Club polls</h3></div>
            <div className="space-y-3">
              {polls.map((p) => {
                const voted = !!p.my_vote;
                return (
                  <div key={p.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`parent-poll-${p.id}`}>
                    <p className="font-heading font-semibold">{p.question}</p>
                    <p className="text-xs text-muted-foreground mb-3">{p.total_votes} vote{p.total_votes === 1 ? "" : "s"}{p.closed ? " · Closed" : ""}</p>
                    <div className="space-y-2">
                      {p.options.map((o) => {
                        const pct = p.total_votes ? Math.round((o.votes / p.total_votes) * 100) : 0;
                        const mine = p.my_vote === o.id;
                        if (voted || p.closed) return (
                          <div key={o.id}>
                            <div className="flex justify-between text-sm mb-1"><span>{o.text}{mine ? " ✓" : ""}</span><span className="text-muted-foreground">{pct}%</span></div>
                            <div className="h-2 rounded-full bg-accent overflow-hidden"><div className={`h-full ${mine ? "brand-gradient" : "bg-muted-foreground/40"}`} style={{ width: `${pct}%` }} /></div>
                          </div>
                        );
                        return <Button key={o.id} variant="outline" className="w-full justify-start" onClick={() => votePoll(p.id, o.id)} data-testid={`vote-${p.id}-${o.id}`}>{o.text}</Button>;
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-6" data-testid="parent-support">
          <div className="flex items-center gap-2 mb-3"><MessageSquare size={18} className="text-secondary" /><h3 className="font-heading font-semibold text-lg">Support &amp; feedback</h3></div>
          <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
            <p className="text-sm text-muted-foreground">Need help or have an idea? Email <a href={`mailto:${support.email}`} className="text-primary underline" data-testid="parent-support-email">{support.email || "support@spherehyx.com"}</a> or send us a note:</p>
            <select value={fb.type} onChange={(e) => setFb({ ...fb, type: e.target.value })} className="h-10 rounded-lg bg-background border border-border px-3 text-sm w-full sm:w-56" data-testid="parent-feedback-type">
              <option value="general">General feedback</option>
              <option value="bug">Report a bug</option>
              <option value="feature">Request a feature</option>
            </select>
            <Input value={fb.subject} onChange={(e) => setFb({ ...fb, subject: e.target.value })} placeholder="Subject" data-testid="parent-feedback-subject" />
            <textarea value={fb.message} onChange={(e) => setFb({ ...fb, message: e.target.value })} rows={3} placeholder="Your message..." className="w-full rounded-lg bg-background border border-border p-3 text-sm" data-testid="parent-feedback-message" />
            <Button onClick={submitFeedback} className="brand-gradient text-white border-0" data-testid="parent-feedback-submit">Send</Button>
          </div>
        </div>

        {data.announcements?.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3"><Megaphone size={18} className="text-secondary" /><h3 className="font-heading font-semibold text-lg">Announcements</h3></div>
            <div className="space-y-3">
              {data.announcements.map((a) => (
                <div key={a.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`parent-ann-${a.id}`}>
                  <p className="font-heading font-semibold">{a.title}</p>
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{a.body}</p>
                  <p className="text-xs text-muted-foreground mt-2">{a.created_at?.slice(0, 10)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
